/**
 * Fragmentation — reassembly of Bitchat type-0x20 fragment packets.
 *
 * Fragment payload layout (BLEService.swift:3456-3480):
 *
 *   Offset  Size  Meaning
 *    0       8    FragmentID     (random, same for all fragments of one message)
 *    8       2    Index          (UInt16 big-endian, 0-based fragment index)
 *   10       2    Total          (UInt16 big-endian, total fragment count)
 *   12       1    OriginalType   (MessageType byte of the reassembled inner packet)
 *   13       …    FragmentData   (opaque bytes)
 *
 * The concatenation of all fragment data (in index order) is itself a
 * COMPLETE BitchatPacket that must be fed back through the normal decoder.
 *
 * Limits mirror iOS (TransportConfig.swift):
 *   - Fragment lifetime:       30 s
 *   - Max in-flight assemblies: 128  (oldest evicted on overflow)
 *   - Total/Index bounds:      Total ∈ [1, 10000]; Index ∈ [0, Total)
 *   - Minimum fragment payload: 13 bytes (header only)
 */

const FRAGMENT_HEADER_SIZE = 13;
const FRAGMENT_LIFETIME_MS = 30 * 1000;
const MAX_ASSEMBLIES       = 128;
const MAX_TOTAL            = 10000;

/**
 * @typedef {Object} FragmentHeader
 * @property {Uint8Array} fragmentId    (8 bytes)
 * @property {number}     index         (0-based)
 * @property {number}     total         (fragment count)
 * @property {number}     originalType  (MessageType byte)
 * @property {Uint8Array} data          (fragment payload bytes)
 */

/**
 * Parse the fragment header + data from the payload of a type-0x20 packet.
 * Returns null if the payload is too short or the fields are out of range.
 */
export function parseFragment(payload) {
  if (!payload || payload.length < FRAGMENT_HEADER_SIZE) return null;
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const fragmentId   = new Uint8Array(payload.subarray(0, 8));
  const index        = view.getUint16(8, false);
  const total        = view.getUint16(10, false);
  const originalType = payload[12];
  const data         = new Uint8Array(payload.subarray(13));
  if (total < 1 || total > MAX_TOTAL) return null;
  if (index >= total) return null;
  return { fragmentId, index, total, originalType, data };
}

export class FragmentReassembler {
  constructor() {
    /** @type {Map<string, { fragments: Map<number, Uint8Array>, total: number, originalType: number, createdAt: number, senderKey: string }>} */
    this.assemblies = new Map();
  }

  /**
   * Accept one fragment. Returns the fully reassembled inner packet bytes
   * when this fragment completes an assembly, otherwise null.
   *
   * @param {Uint8Array} senderId  the 8-byte senderID of the outer packet
   * @param {Uint8Array} payload   the payload of the outer type-0x20 packet
   * @returns {{ bytes: Uint8Array, originalType: number } | null}
   */
  ingest(senderId, payload) {
    const frag = parseFragment(payload);
    if (!frag) return null;

    this._gc();

    const key = _hex(senderId) + ':' + _hex(frag.fragmentId);
    let asm = this.assemblies.get(key);
    if (!asm) {
      asm = {
        fragments: new Map(),
        total: frag.total,
        originalType: frag.originalType,
        createdAt: Date.now(),
        senderKey: _hex(senderId),
      };
      this.assemblies.set(key, asm);
      this._evictOldestIfOverflow();
    }

    if (frag.total !== asm.total) {
      // Inconsistent — drop the assembly and start fresh with this fragment.
      this.assemblies.delete(key);
      return null;
    }

    asm.fragments.set(frag.index, frag.data);

    if (asm.fragments.size < asm.total) return null;

    // Complete — concatenate in index order.
    const parts = [];
    for (let i = 0; i < asm.total; i++) {
      const part = asm.fragments.get(i);
      if (!part) {
        // Missing fragment — shouldn't happen if size === total, but guard.
        this.assemblies.delete(key);
        return null;
      }
      parts.push(part);
    }
    this.assemblies.delete(key);
    return { bytes: _concatAll(parts), originalType: asm.originalType };
  }

  _gc() {
    const cutoff = Date.now() - FRAGMENT_LIFETIME_MS;
    for (const [key, asm] of this.assemblies.entries()) {
      if (asm.createdAt < cutoff) this.assemblies.delete(key);
    }
  }

  _evictOldestIfOverflow() {
    if (this.assemblies.size <= MAX_ASSEMBLIES) return;
    // Map preserves insertion order — first key is oldest.
    const firstKey = this.assemblies.keys().next().value;
    if (firstKey !== undefined) this.assemblies.delete(firstKey);
  }
}

// ---------- helpers ----------

function _hex(u8) {
  return Array.from(u8, b => b.toString(16).padStart(2, '0')).join('');
}

function _concatAll(arrs) {
  let total = 0;
  for (const a of arrs) total += a.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const a of arrs) { out.set(a, o); o += a.length; }
  return out;
}

export default { FragmentReassembler, parseFragment };
